import { useRef, useState } from "react";
import { Loader2, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("http") || u.startsWith("data:")) return u;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${u}`;
}

/**
 * Combined "upload image OR paste URL" field for the admin prompt form.
 * Sits above the plain URL input; uploading a file auto-fills the URL.
 */
export default function ImageUploadField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const preview = resolveUrl(value);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Only image files are allowed");
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Image must be under 8 MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/prompts/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.image_url);
      toast.success("Image uploaded", {
        description: `${(data.size / 1024).toFixed(0)} KB · ready to submit`,
      });
    } catch (err) {
      toast.error("Upload failed", {
        description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-3">
      <div
        data-testid="prompt-image-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const f = e.dataTransfer.files?.[0];
          if (f) handleFile(f);
        }}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`relative rounded-xl border-2 border-dashed transition-colors cursor-pointer overflow-hidden ${
          dragOver
            ? "border-[#E0007A] bg-[#E0007A]/5"
            : "border-white/15 hover:border-white/30 bg-black/20"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          data-testid="prompt-image-file-input"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />

        {preview ? (
          <div className="relative w-full aspect-[4/5] max-h-64 bg-[#0E0E12]">
            <img
              src={preview}
              alt="preview"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {uploading && (
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-[#00E5FF]" />
              </div>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              data-testid="prompt-image-clear"
              aria-label="Clear image"
              className="absolute top-2 right-2 h-8 w-8 rounded-full bg-black/70 hover:bg-black text-white/80 hover:text-white flex items-center justify-center transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
            <span className="absolute bottom-2 left-2 text-[10px] font-mono uppercase tracking-widest text-white/70 bg-black/50 px-2 py-0.5 rounded">
              tap to replace
            </span>
          </div>
        ) : (
          <div className="p-8 flex flex-col items-center justify-center text-center">
            {uploading ? (
              <Loader2 className="h-6 w-6 animate-spin text-[#00E5FF]" />
            ) : (
              <ImagePlus className="h-7 w-7 text-white/40" />
            )}
            <div className="mt-3 font-medium">
              {uploading ? "Uploading…" : "Drop an image or click to browse"}
            </div>
            <div className="mt-1 text-xs text-white/40 font-mono">
              JPG · PNG · WEBP · GIF · max 8 MB
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
